// Vertical placement of one column of callouts.
//
// Given n items already in the order they must appear top to bottom, each with a
// measured height `h[i]` and a preferred centre `y*[i]`, choose centres `y[i]`
// minimising Σ(y[i] − y*[i])² subject to
//
//     y[i+1] − y[i]  >=  (h[i] + h[i+1]) / 2 + gap     no overlap, order kept
//     ymin + h[i]/2  <=  y[i]  <=  ymax − h[i]/2       on screen
//
// Substituting `z[i] = y[i] − H[i]`, where H accumulates the minimum separations,
// turns the first constraint into "z non-decreasing" — isotonic regression, which
// pool-adjacent-violators solves exactly in O(n). Heights disappear into H, so
// items of differing heights need no special handling.
//
// The two-sided bounds then collapse to a single box on z, because
//
//     lo[i+1] − lo[i] = −h[i]   − gap  <  0
//     hi[i+1] − hi[i] = −h[i+1] − gap  <  0
//
// are both strictly decreasing: for a non-decreasing z only the first lower bound
// and the last upper bound can ever bind. Projecting onto a uniform box is just
// clamping, so "PAVA, then clamp" is the exact answer and not an iteration.
//
// The result is the Euclidean projection onto a convex set, and so is 1-Lipschitz
// in y*: one callout arriving or resizing moves the others by no more than it
// moved itself. The previous solver anchored on whichever item happened to sit
// past the mean, so a single arrival re-picked the anchor and shifted the whole
// column. That is the difference between the stack drifting and the stack
// jumping.

const MAX_STACK = 64;

const work = new Float64Array(MAX_STACK);
const blockSum = new Float64Array(MAX_STACK);
const blockCount = new Int32Array(MAX_STACK);
const blockEnd = new Int32Array(MAX_STACK);

export const MAX_STACK_SIZE = MAX_STACK;

// Total vertical extent n items need. The feasibility test the caller uses to
// decide whether anything has to be dropped.
export function requiredHeight(n, heights, gap) {
  if (n <= 0) return 0;
  let total = (n - 1) * gap;
  for (let i = 0; i < n; i++) total += heights[i];
  return total;
}

// Cumulative minimum separations, `offsets[0] = 0`. Exposed because the caller
// needs the same values to convert a previous frame's centres back into z for
// smoothing.
export function separationOffsets(n, heights, gap, offsets) {
  if (n <= 0) return;
  offsets[0] = 0;
  for (let i = 1; i < n; i++) {
    offsets[i] = offsets[i - 1] + (heights[i - 1] + heights[i]) * 0.5 + gap;
  }
}

// Solve for centres. `offsets` must already hold `separationOffsets`, which is
// where the gap has gone.
export function solve(n, ideal, heights, ymin, ymax, offsets, out) {
  if (n <= 0) return;

  for (let i = 0; i < n; i++) work[i] = ideal[i] - offsets[i];

  poolAdjacentViolators(n, work);
  clampToBox(n, work, heights, ymin, ymax, offsets);

  for (let i = 0; i < n; i++) out[i] = work[i] + offsets[i];
}

// Move `from` a fraction `alpha` of the way to `to`, in z space rather than in y.
// A convex combination of two non-decreasing sequences is non-decreasing, so
// every intermediate frame satisfies the separation constraints exactly — which
// smoothing the centres directly would not guarantee.
export function blendTowards(
  n,
  fromY,
  toY,
  alpha,
  heights,
  ymin,
  ymax,
  offsets,
  out
) {
  if (n <= 0) return;

  for (let i = 0; i < n; i++) work[i] = fromY[i] - offsets[i];

  // The previous frame's centres were solved against a different set — an item
  // may have arrived, left or been re-measured — so re-derived z is not
  // necessarily monotone any more. Restoring that first is what keeps the blend
  // a combination of two feasible points.
  poolAdjacentViolators(n, work);

  for (let i = 0; i < n; i++) {
    work[i] += (toY[i] - offsets[i] - work[i]) * alpha;
  }

  clampToBox(n, work, heights, ymin, ymax, offsets);

  for (let i = 0; i < n; i++) out[i] = work[i] + offsets[i];
}

function poolAdjacentViolators(n, v) {
  let top = -1;

  for (let i = 0; i < n; i++) {
    top++;
    blockSum[top] = v[i];
    blockCount[top] = 1;
    blockEnd[top] = i;

    while (
      top > 0 &&
      blockSum[top - 1] / blockCount[top - 1] > blockSum[top] / blockCount[top]
    ) {
      blockSum[top - 1] += blockSum[top];
      blockCount[top - 1] += blockCount[top];
      blockEnd[top - 1] = blockEnd[top];
      top--;
    }
  }

  let i = 0;
  for (let b = 0; b <= top; b++) {
    const mean = blockSum[b] / blockCount[b];
    for (; i <= blockEnd[b]; i++) v[i] = mean;
  }
}

function clampToBox(n, v, heights, ymin, ymax, offsets) {
  const lower = ymin + heights[0] * 0.5;
  let upper = ymax - heights[n - 1] * 0.5 - offsets[n - 1];

  // One item taller than the whole range, or a column that cannot fit. Accept
  // the overlap rather than producing an inverted range and NaNs downstream;
  // the caller's capacity check is what is meant to stop this happening.
  if (upper < lower) upper = lower;

  for (let i = 0; i < n; i++) {
    if (v[i] < lower) v[i] = lower;
    else if (v[i] > upper) v[i] = upper;
  }
}
